import { useAuth0 } from "@auth0/auth0-react";
import { IconInfoCircle } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/core/Buttons/Button";
import ButtonGroup from "@/components/core/Buttons/ButtonGroup";
import InputField from "@/components/core/FormControls/InputField";
import Loader from "@/components/core/Loaders/Loader";
import InfoToolsSection from "@/components/Home/InfoToolsSection";
import HomepageInstructions from "@/components/Home/InstructionsPopup";
import LocationPickerModal from "@/components/Home/LocationPicker";
import ManualIdentificationInstructions from "@/components/Home/ManualIdentificationInstructions";
import SessionInStepPopup from "@/components/Home/SessionInStepPopup";
import DisplayWebAppQRCode from "@/components/Home/WebAppQRCode";
import { useDeviceSubmission } from "@/hooks/useDeviceSubmission";
import { useHomeQueries } from "@/hooks/useHomeQueries";
import { useTagManager } from "@/hooks/useTagManager";
import { requireAuthOnly } from "@/lib/guards";
import { useStore } from "@/store";
import logger from "@/utils/logging/logger";
import { clearStorageData } from "@/utils/session/storage";

export const Route = createFileRoute("/$locale/$partner/home")({
	beforeLoad: requireAuthOnly,
	component: Home,
});

function Home() {
	const { t } = useTranslation();
	const { user } = useAuth0();
	const rawCategories = useStore((state) => state.config?.partnerSettings.supportedDeviceTypes) ?? [];
	const categories = rawCategories.map((cat) => cat.toLowerCase() as typeof cat);
	const imei = useStore((state) => state.deviceInfo?.imei ?? "");
	const category = useStore((state) => state.category);
	const hasPickedLocation = useStore((state) => state.hasPickedLocation);
	const setDeviceInfo = useStore((state) => state.setDeviceInfo);
	const setAttribute = useStore((state) => state.setAttribute);

	const [openInfoModal, setOpenInfoModal] = useState(false);

	const platformRoles = user?.platform_roles;
	const isAdmin = (Array.isArray(platformRoles) && platformRoles.includes("RetailApp Admin")) ?? false;

	useTagManager("Step Home");

	const { currentLocation, supportedAssessments, isLoading } = useHomeQueries();

	const {
		error,
		loading,
		disabled,
		isSessionInStepTransaction,
		setIsSessionInStepTransaction,
		voucherCode,
		handleSubmitImei,
		clearError,
	} = useDeviceSubmission({ currentLocation, supportedAssessments });

	useEffect(() => {
		clearStorageData();
		if (isAdmin) {
			logger.injectCustomProp("pandas_shadowing", 1);
		}
	}, [isAdmin]);

	const handleImeiInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		clearError();
		setDeviceInfo({ imei: event.target.value });
	};

	return (
		<>
			{isLoading ? (
				<div className="absolute w-fit h-fit top-1/3 left-1/2">
					<Loader />
				</div>
			) : isAdmin && !currentLocation?.id ? (
				<LocationPickerModal showPopup={!hasPickedLocation} />
			) : (
				<>
					<div className="flex flex-1 flex-col">
						<div className="select-none justify-center text-center px-4 flex flex-col items-center mx-auto my-10">
							{categories.length > 1 && (
								<div className="max-w-[28rem] w-full">
									<ButtonGroup
										buttons={categories}
										activeButton={category}
										setActiveButton={(value) => {
											if (value !== category) {
												clearError();
											}
											setAttribute("category", value);
										}}
									/>
								</div>
							)}
							<h1 className="text-3xl font-semibold mt-16 mb-5">{t("Homepage.Welcome")}</h1>
							<div className="flex items-center">
								<p className="text-xl text-[#808080] pr-1">
									{t("Homepage.Enter the IMEI or Serial Number to assess the device.")}
								</p>
								<div
									className="cursor-pointer"
									onClick={() => setOpenInfoModal(true)}
								>
									<IconInfoCircle
										width={24}
										height={24}
										stroke={1}
										color="gray"
									/>
								</div>
							</div>
							<div className="max-w-xl w-full">
								<div className={`mt-10 ${error ? "mb-20" : "mb-10"}`}>
									<InputField
										placeholder={t("InputField.IMEI Serial number")}
										type="text"
										value={imei ?? ""}
										error={error}
										onChange={handleImeiInput}
										onKeyPress={(e) => {
											if (e.key === "Enter" && !disabled && imei && imei.length >= 8) {
												handleSubmitImei();
											}
										}}
									/>
								</div>
							</div>
							<div className="mt-1 mx-auto">
								<Button
									disabled={disabled || !imei || imei.length < 8}
									loading={loading}
									label={t("Buttons.Let's start")}
									onClick={handleSubmitImei}
								/>
							</div>
							<hr className="w-full max-w-xl my-12 border-t-2 border-grey" />
							{currentLocation?.id && (
								<DisplayWebAppQRCode
									locationId={currentLocation.id}
									category={category}
								/>
							)}
							{["tablet", "smartwatch"].includes(category) && <ManualIdentificationInstructions />}
						</div>
						<InfoToolsSection />
					</div>
					<HomepageInstructions
						isOpen={openInfoModal}
						setIsOpen={setOpenInfoModal}
						category={category}
					/>
					<SessionInStepPopup
						isOpen={isSessionInStepTransaction}
						setIsOpen={setIsSessionInStepTransaction}
						voucherCode={voucherCode}
					/>
				</>
			)}
		</>
	);
}
